import {
  DashboardCogsDetailResponse,
  DashboardSalesDetailResponse,
  SalesDetailOrder,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const fmtD = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
};

const paymentStatusKo: Record<string, string> = {
  Paid: '결제완료',
  Confirmed: '확인됨',
  Canceled: '취소됨',
  Failed: '실패',
  Pending: '대기중',
};

const paymentMethodKo: Record<string, string> = {
  card: '카드',
  vbank: '가상계좌',
  trans: '계좌이체',
  phone: '휴대폰',
  cash: '현금',
};

const couponStatusKo: Record<string, string> = {
  Requested: '요청됨',
  Delivered: '전달완료',
  Refunded: '환불완료',
  Available: '사용가능',
  Rejected: '거절됨',
  Expired: '만료됨',
  Canceled: '취소됨',
};

const deliveryStatusKo: Record<string, string> = {
  WaitingAddress: '주소 대기',
  AddressSubmitted: '주소 입력됨',
  Preparing: '준비중',
  Shipping: '배송중',
  Delivered: '배송완료',
  Canceled: '취소됨',
};

const NUM_FMT = '#,##0';

function charWidth(s: string): number {
  let w = 0;
  for (const ch of s) {
    w += ch.charCodeAt(0) > 0x7f ? 2.2 : 1;
  }
  return w;
}

function autoWidth(ws: XLSX.WorkSheet) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  const cols: XLSX.ColInfo[] = [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    let max = 6;
    for (let R = range.s.r; R <= range.e.r; R++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
      if (cell?.v != null) {
        const w = charWidth(String(cell.v));
        if (w > max) max = w;
      }
    }
    cols.push({ wch: Math.min(Math.ceil(max) + 3, 45) });
  }
  ws['!cols'] = cols;
}

function applyNumberFormat(ws: XLSX.WorkSheet, numCols: number[]) {
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let R = range.s.r + 1; R <= range.e.r; R++) {
    for (let i = 0; i < numCols.length; i++) {
      const C = numCols[i];
      if (C === undefined) continue;
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (cell && typeof cell.v === 'number') {
        cell.z = NUM_FMT;
      }
    }
  }
}

function freezeHeader(ws: XLSX.WorkSheet) {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  if (!ws['!views']) ws['!views'] = [];
  (ws['!views'] as Array<Record<string, unknown>>)[0] = {
    state: 'frozen',
    ySplit: 1,
  };
}

function findNumCols(ws: XLSX.WorkSheet, labels: string[]): number[] {
  const result: number[] = [];
  const range = XLSX.utils.decode_range(ws['!ref'] ?? 'A1');
  for (let C = range.s.c; C <= range.e.c; C++) {
    const hdr = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
    if (hdr && labels.includes(String(hdr.v))) result.push(C);
  }
  return result;
}

function finishSheet(ws: XLSX.WorkSheet, moneyLabels: string[]) {
  autoWidth(ws);
  freezeHeader(ws);
  const numCols = findNumCols(ws, moneyLabels);
  if (numCols.length > 0) applyNumberFormat(ws, numCols);
}

function saveWorkbook(wb: XLSX.WorkBook, filename: string) {
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
}

/* ── 매출 상세 엑셀 ── */

const SALES_MONEY_LABELS = [
  '결제금',
  '취소금',
  '면세 매출',
  '과세 매출',
  '총 매출',
  '수수료',
  '면세 잔액',
  '과세 잔액',
  '잔액(매출-수수료)',
  '도매가 합계',
  '수익금',
];

const ITEM_MONEY_LABELS = ['소비자가'];

function buildSalesOrderRow(o: SalesDetailOrder, idx: number, hideReceiver: boolean) {
  const r: Record<string, unknown> = { '#': idx + 1, 결제일: fmtD(o.purchasedAt) };
  if (!hideReceiver) {
    r['구매자'] = o.receiverName;
    r['구매자 연락처'] = o.receiverPhoneNumber;
  }
  r['고인명'] = o.deceasedName;
  r['발인일'] = fmtD(o.coffinOutDate);
  r['행사등록자'] = o.feventUserName;
  r['행사등록자 연락처'] = o.feventUserPhone;
  r['결제상태'] = paymentStatusKo[o.paymentStatus] ?? o.paymentStatus;
  r['결제수단'] = paymentMethodKo[o.paymentMethod] ?? o.paymentMethod;
  r['결제금'] = o.paymentByCash;
  r['취소금'] = o.cancelledTotalPrice;
  r['면세 매출'] = o.netTaxFree;
  r['과세 매출'] = o.netTaxable;
  r['총 매출'] = o.grossSales;
  r['수수료'] = o.serviceFee;
  r['면세 잔액'] = o.taxFreeBalance ?? 0;
  r['과세 잔액'] = o.taxableBalance ?? 0;
  r['잔액(매출-수수료)'] = o.balanceAfterFee;
  r['도매가 합계'] = o.totalWholesale;
  r['수익금'] = o.balanceAfterFee - o.totalWholesale;
  return r;
}

function buildSalesItemRows(
  o: SalesDetailOrder,
  orderIdx: number,
  hideReceiver: boolean,
) {
  return o.items.map((item) => {
    const r: Record<string, unknown> = {};
    if (hideReceiver) {
      r['주문 순번'] = orderIdx + 1;
    } else {
      r['주문ID'] = o.orderId;
    }
    r['결제일'] = fmtD(o.purchasedAt);
    if (!hideReceiver) {
      r['구매자'] = o.receiverName;
    }
    r['고인명'] = o.deceasedName;
    r['상품명'] = item.giftItemName;
    r['유형'] = item.type === 'Coupon' ? '쿠폰' : '배송';
    r['수량'] = item.quantity;
    r['소비자가'] = item.priceRetail;
    r['과세여부'] = item.isTaxable ? '과세' : '면세';
    return r;
  });
}

export function exportSalesDetailExcel(
  data: DashboardSalesDetailResponse,
  periodLabel: string,
  hideReceiver = false,
) {
  const wb = XLSX.utils.book_new();

  const totalTaxFree = data.orders.reduce((s, o) => s + o.netTaxFree, 0);
  const totalTaxable = data.orders.reduce((s, o) => s + o.netTaxable, 0);

  const summaryRows = [
    { 항목: '조회 기간', 값: periodLabel },
    { 항목: '주문 수', 값: data.orderCount },
    { 항목: '총 매출', 값: data.totalGrossSales },
    { 항목: '면세 매출', 값: totalTaxFree },
    { 항목: '과세 매출', 값: totalTaxable },
    { 항목: '총 수수료', 값: data.totalServiceFee },
    { 항목: '매출 - 수수료', 값: data.totalBalanceAfterFee },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  finishSheet(wsSummary, ['값']);
  XLSX.utils.book_append_sheet(wb, wsSummary, '요약');

  const orderRows = data.orders.map((o, i) => buildSalesOrderRow(o, i, hideReceiver));
  const ws1 = XLSX.utils.json_to_sheet(orderRows);
  finishSheet(ws1, SALES_MONEY_LABELS);
  XLSX.utils.book_append_sheet(wb, ws1, '매출 상세');

  const itemRows = data.orders.flatMap((o, i) => buildSalesItemRows(o, i, hideReceiver));
  if (itemRows.length > 0) {
    const ws2 = XLSX.utils.json_to_sheet(itemRows);
    finishSheet(ws2, ITEM_MONEY_LABELS);
    XLSX.utils.book_append_sheet(wb, ws2, '주문별 상품');
  }

  saveWorkbook(wb, `매출상세_${periodLabel}.xlsx`);
}

/* ── 매입 상세 엑셀 ── */

const COGS_COUPON_MONEY = ['소비자가', '도매가'];
const COGS_DELIVERY_MONEY = ['단가', '도매가'];
const COGS_SUMMARY_MONEY = ['금액'];

export function exportCogsDetailExcel(
  data: DashboardCogsDetailResponse,
  periodLabel: string,
  hideReceiver = false,
) {
  const wb = XLSX.utils.book_new();

  const summaryRows = [
    { 항목: '조회 기간', 금액: periodLabel },
    { 항목: '쿠폰 매입 합계', 금액: data.couponTotalWholesale },
    { 항목: '배송 매입 합계', 금액: data.deliveryTotalWholesale },
    { 항목: '', 금액: '' },
    { 항목: '면세 매입 합계', 금액: data.taxFreeCogs },
    { 항목: '과세 매입 합계', 금액: data.taxableCogs },
    { 항목: '', 금액: '' },
    { 항목: '총 매입 원가', 금액: data.totalCogs },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
  finishSheet(wsSummary, COGS_SUMMARY_MONEY);
  XLSX.utils.book_append_sheet(wb, wsSummary, '매입 요약');

  const couponRows = data.coupons.map((c, i) => {
    const r: Record<string, unknown> = { '#': i + 1, 상품명: c.giftItemName };
    if (!hideReceiver) {
      r['수령자'] = c.recipientName;
      r['수령자 연락처'] = c.recipientPhone;
    }
    r['지도사'] = c.bugoUserName ?? '';
    r['지도사 연락처'] = c.bugoUserPhone ?? '';
    r['전달일'] = fmtD(c.fulfilledAt);
    r['구매일'] = fmtD(c.purchasedAt);
    r['상태'] = couponStatusKo[c.redeemStatus] ?? c.redeemStatus;
    r['과세여부'] = c.isTaxable ? '과세' : '면세';
    r['소비자가'] = c.priceRetail;
    r['도매가'] = c.priceWhole;
    return r;
  });
  const ws1 = XLSX.utils.json_to_sheet(
    couponRows.length > 0
      ? couponRows
      : [{ 안내: '해당 기간 쿠폰 매입 내역이 없습니다.' }],
  );
  finishSheet(ws1, COGS_COUPON_MONEY);
  XLSX.utils.book_append_sheet(wb, ws1, '쿠폰 매입');

  const deliveryRows = data.deliveries.map((d, i) => {
    const r: Record<string, unknown> = { '#': i + 1, 상품명: d.giftItemName };
    if (!hideReceiver) {
      r['수령자'] = d.recipientName;
      r['수령자 연락처'] = d.recipientPhone;
    }
    r['행사팀장'] = d.bugoUserName ?? '';
    r['행사팀장 연락처'] = d.bugoUserPhone ?? '';
    r['배송완료일'] = fmtD(d.deliveredAt);
    r['상태'] = deliveryStatusKo[d.deliveryStatus] ?? d.deliveryStatus;
    r['과세여부'] = d.isTaxable ? '과세' : '면세';
    r['수량'] = d.quantity;
    r['단가'] = d.priceWhole;
    r['도매가'] = d.lineCogs;
    return r;
  });
  const ws2 = XLSX.utils.json_to_sheet(
    deliveryRows.length > 0
      ? deliveryRows
      : [{ 안내: '해당 기간 배송 매입 내역이 없습니다.' }],
  );
  finishSheet(ws2, COGS_DELIVERY_MONEY);
  XLSX.utils.book_append_sheet(wb, ws2, '배송 매입');

  saveWorkbook(wb, `매입상세_${periodLabel}.xlsx`);
}
